import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import { historicalDataApi } from '../lib/api/historicalDataApi';

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

const metrics = [
  { key: "signalStrength", label: "Signal Strength (dBm)", type: "line", category: "signal" },
  { key: "lteSignalStrength", label: "LTE Signal Strength (dBm)", type: "line", category: "signal" },
  { key: "lanLatencyMs", label: "LAN Latency (ms)", type: "line", category: "latency" },
  { key: "latency4GMs", label: "4G Latency (ms)", type: "line", category: "latency" },
  { key: "chPingAvg", label: "Chicago Ping Avg (ms)", type: "line", category: "latency" },
  { key: "laPingAvg", label: "LA Ping Avg (ms)", type: "line", category: "latency" },
  { key: "nyPingAvg", label: "NY Ping Avg (ms)", type: "line", category: "latency" },
  { key: "lteUploadTCP", label: "LTE Upload (TCP) (Mbps)", type: "line", category: "bandwidth" },
  { key: "lteDownloadTCP", label: "LTE Download (TCP) (Mbps)", type: "line", category: "bandwidth" },
  { key: "lteUploadUDP", label: "LTE Upload (UDP) (Mbps)", type: "line", category: "bandwidth" },
  { key: "lteDownloadUDP", label: "LTE Download (UDP) (Mbps)", type: "line", category: "bandwidth" },
  // { key: "tempCelsius", label: "Temperature (°C)", type: "line", category: "temperature" },
  { key: "cpuTempCelsius", label: "CPU Temperature (°C)", type: "line", category: "temperature" },
  { key: "devicePowerW", label: "Device Power (W)", type: "line", category: "power" },
  { key: "uptimeSeconds", label: "Uptime (s)", type: "line", category: "system" },
  { key: "everythingOnline", label: "Everything Online", type: "step", category: "status" },
  { key: "apiOnline", label: "API Online", type: "step", category: "status" },
  { key: "monitor", label: "Monitor Online", type: "step", category: "status" },
  { key: "alarmed", label: "Alarmed", type: "step", category: "alerts" },
  { key: "registered", label: "Registered", type: "step", category: "status" },
];

const categories = [...new Set(metrics.map(metric => metric.category))];
const defaultCategory = "temperature";

const HistoricalCharts = ({ deviceId }) => {
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
  const [timeRange, setTimeRange] = useState("24h"); // Default to 24h
  const [historicalData, setHistoricalData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [visibleMetrics, setVisibleMetrics] = useState(() =>
     metrics.filter(metric => metric.category === defaultCategory)
  );
  const [deviceInfo, setDeviceInfo] = useState({
    name: "Loading...",
    serialNumber: "Loading...",
    model: "Loading..."
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [latestValues, setLatestValues] = useState({});
  
  useEffect(() => {
    const fetchData = async () => {
      if (!deviceId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await historicalDataApi.fetchAllHistoricalData(deviceId, timeRange, visibleMetrics);
        
        setHistoricalData(data);
        
        if (data && data.length > 0) {
          const firstRecord = data[0];
          setDeviceInfo({
            name: firstRecord.displayName || "Unknown Device",
            serialNumber: firstRecord.serialNumber || "Unknown Serial",
            model: firstRecord.model || "Unknown Model"
          });
        }
      } catch (err) {
        console.error("Failed to fetch historical data:", err);
        setError(err.message || "Failed to fetch historical data");
        setHistoricalData([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [deviceId, timeRange, visibleMetrics]);

  useEffect(() => {
    const fetchLatestValues = async () => {
      if (!deviceId || !visibleMetrics.length) return;
      
      try {
        const columns = visibleMetrics.map(m => m.key);
        const data = await historicalDataApi.fetchHistoricalDataLastValue({
          deviceId,
          columns
        });
        setLatestValues(data[0] || {});
      } catch (err) {
        console.error("Failed to fetch latest values:", err);
      }
    };

    fetchLatestValues();
  }, [deviceId, visibleMetrics]);

  useEffect(() => {
    if (!historicalData || historicalData.length === 0) {
        setFilteredData([]);
        return;
    }

    const now = new Date();
    let cutoff;

    switch (timeRange) {
      case "7d":
        cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "30d":
        cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case "all":
         cutoff = new Date(0); 
         break;
      case "24h":
      default:
        cutoff = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
    }

    const dataWithParsedTimestamps = historicalData.map(item => ({
        ...item,
        parsedTimestamp: new Date(item.timestamp.replace(' ', 'T') + 'Z') 
    })).filter(item => !isNaN(item.parsedTimestamp));

    setFilteredData(
      dataWithParsedTimestamps.filter(item => item.parsedTimestamp >= cutoff)
    );
  }, [timeRange, historicalData]);

  useEffect(() => {
    setVisibleMetrics(metrics.filter(metric => metric.category === selectedCategory));
  }, [selectedCategory]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp.replace(' ', 'T') + 'Z');
    if (isNaN(date)) return "Invalid Date";

    if (timeRange === "24h") {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }); // Use AM/PM
    } else if (timeRange === "7d") {
      return date.toLocaleDateString([], { weekday: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const timestamps = filteredData.map(d => formatTimestamp(d.timestamp));

  const getChartColor = (index) => {
    const baseColors = [
      "#a3c9f9", 
      "#a9e7c4", 
      "#f6a9a9", 
      "#ffccab", 
      "#c8b4e4",
      "#a9ede4",
      "#cabdf4",
      "#f8c0d9",
      "#fff3b3",
      "#b0e1eb"
    ];
    
    return baseColors[index % baseColors.length];
  };

  const isValidValue = (value) => {
    return value !== null && value !== undefined && value !== "No Data";
  };

  const numericMetrics = visibleMetrics.filter(metric => metric.type === "line");
  const booleanMetrics = visibleMetrics.filter(metric => metric.type === "step");

  const getYAxisRange = () => {
    if (numericMetrics.length === 0 || filteredData.length === 0) return { min: undefined, max: undefined };

    let allValues = [];
    numericMetrics.forEach(metric => {
      const values = filteredData
        .map(d => d[metric.key])
        .filter(isValidValue);
      allValues = [...allValues, ...values];
    });

    if (allValues.length === 0) return { min: undefined, max: undefined };

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    const range = max - min;
    const padding = Math.max(range * 0.1, Math.abs(max + min) * 0.05, 1);

    const forceZero = numericMetrics.some(m =>
        ["latency", "bandwidth", "power", "system"].includes(m.category) || m.key === "signalStrength"
    );

    return {
      min: forceZero ? Math.min(0, min - padding) : min - padding, 
      max: max + padding
    };
  };

  const yAxisRange = getYAxisRange();

  const numericDatasets = numericMetrics.map((metric, index) => ({
    label: metric.label,
    data: filteredData.map(d => {
      const value = d[metric.key];
      return isValidValue(value) ? value : null;
    }),
    borderColor: getChartColor(index),
    backgroundColor: getChartColor(index),
    tension: 0.2,
    pointRadius: filteredData.length > 50 ? 2 : 4,
    spanGaps: true
  }));

  const booleanDatasets = booleanMetrics.map((metric, index) => ({
    label: metric.label,

    data: filteredData.map(d => {
        const value = d[metric.key];
        if (value === 1 || value === true) return 1;
        if (value === 0 || value === false) return 0;
        return null;
    }),
    borderColor: getChartColor(index),
    backgroundColor: getChartColor(index),
    stepped: true,
    pointRadius: filteredData.length > 50 ? 2 : 4,
    spanGaps: false
  }));

   const xAxisConfig = {
    title: { display: true, text: "Time" },
    ticks: {
      maxRotation: 45,
      minRotation: 45,
      maxTicksLimit: timeRange === '24h' ? 12 : 20, 
      autoSkip: true,

    },
    grid: {
      color: 'rgba(0, 0, 0, 0.1)'
    }
  };

  const baseChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 500
      },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            boxWidth: 12,
            usePointStyle: true,
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(context) {
              const label = context.dataset.label || '';
              const value = context.raw;
              if (!isValidValue(value)) {
                  return `${label}: No Data`;
              }

              if (context.dataset.stepped) {
                  return `${label}: ${value === 1 ? 'Yes' : 'No'}`;
              }
              return `${label}: ${value}`;
            }
          }
        },
        title: { 
          display: true,
          font: {
            size: 16
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
  };

  const numericChartOptions = {
      ...baseChartOptions,
      plugins: {
        ...baseChartOptions.plugins,
        title: {
           ...baseChartOptions.plugins.title,
           text: `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Metrics - ${deviceInfo.name}`,
        }
      },
      scales: {
        x: xAxisConfig,
        y: {
          title: {
            display: true,
            text: numericMetrics.length === 1
                    ? numericMetrics[0].label.split('(')[1]?.replace(')', '') || "Value"
                    : `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Value`
          },
          min: yAxisRange.min,
          max: yAxisRange.max,
          grid: {
             color: 'rgba(0, 0, 0, 0.1)'
          }
        },
      },
  };


  const booleanChartOptions = {
     ...baseChartOptions,
      plugins: {
         ...baseChartOptions.plugins,
        title: {
            ...baseChartOptions.plugins.title,
            text: `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Status - ${deviceInfo.name}`,
        }
      },
      scales: {
        x: xAxisConfig,
        y: {
          title: { display: true, text: "Status" },
          min: -0.1, 
          max: 1.1, 
          ticks: {
            stepSize: 1, 
            callback: function(value) {
               if (value === 0) return 'No';
               if (value === 1) return 'Yes';
               return ''; 
            }
          },
           grid: {
             color: 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
  };

  const hasValidNumericData = numericDatasets.some(dataset =>
    dataset.data.some(value => value !== null)
  );

  const hasValidBooleanData = booleanDatasets.some(dataset =>
    dataset.data.some(value => value !== null)
  );

  return (
    <div className="historical-charts" style={{ fontFamily: 'sans-serif', padding: 20 }}>
      <div className="device-info" style={{ marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid #eee' }}>
        <h3>{deviceInfo.name}</h3>
        <div style={{ fontSize: 14, color: "#6c757d" }}>
          Serial: {deviceInfo.serialNumber} | Model: {deviceInfo.model}
        </div>
      </div>

      <div className="latest-values" style={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: 16, 
        marginBottom: 20, 
        padding: 16, 
        backgroundColor: '#f8f9fa', 
        borderRadius: 8 
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {visibleMetrics.map(metric => (
            <div key={metric.key} style={{ 
              minWidth: 200,
              padding: 8,
              borderRadius: 4,
              backgroundColor: 'white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: 14, color: '#666' }}>{metric.label}</div>
              <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                {latestValues[metric.key] !== undefined 
                  ? (metric.type === 'step' 
                      ? (latestValues[metric.key] ? 'Yes' : 'No')
                      : latestValues[metric.key])
                  : 'N/A'}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="filter-controls" style={{ marginBottom: 20, display: "flex", flexWrap: 'wrap', gap: 16 }}>
        <div>
          <label htmlFor="category-select" style={{ marginRight: 8 }}>Metric Category:</label>
          <select
            id="category-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="time-range" style={{ marginRight: 8 }}>Time Range:</label>
          <select
            id="time-range"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>
      </div>

      {filteredData.length === 0 && historicalData.length > 0 ? (
        <div style={{ textAlign: 'center', padding: 20, backgroundColor: '#f8f9fa', borderRadius: 4, marginTop: 20 }}>
          No data available for the selected time range.
        </div>
      ) : !historicalData || historicalData.length === 0 ? (
         <div style={{ textAlign: 'center', padding: 20, backgroundColor: '#f8f9fa', borderRadius: 4, marginTop: 20 }}>
          Waiting for data...
        </div>
      ): (
        <>
          {numericMetrics.length > 0 && (
            <div style={{ marginBottom: 30 }}>
              <div style={{ position: 'relative', height: 400, marginBottom: 20 }}>
                {hasValidNumericData ? (
                  <Line
                    data={{
                      labels: timestamps,
                      datasets: numericDatasets,
                    }}
                    options={numericChartOptions}
                  />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', backgroundColor: '#f8f9fa', borderRadius: 4 }}>
                    <p>No valid numeric data available for this category and time range.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {booleanMetrics.length > 0 && (
            <div style={{ marginBottom: 30 }}>
              <div style={{ position: 'relative', height: 300 }}>
                {hasValidBooleanData ? (
                  <Line
                    data={{
                      labels: timestamps,
                      datasets: booleanDatasets,
                    }}
                    options={booleanChartOptions}
                  />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', backgroundColor: '#f8f9fa', borderRadius: 4 }}>
                    <p>No valid status data available for this category and time range.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {numericMetrics.length === 0 && booleanMetrics.length === 0 && (
            <div style={{ textAlign: 'center', padding: 20, backgroundColor: '#f8f9fa', borderRadius: 4, marginTop: 20 }}>
              No metrics are defined for the selected category: {selectedCategory}.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HistoricalCharts;